import { ChangeDetectorRef, Component, ElementRef, Inject, Input, LOCALE_ID, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { chartReport } from "app/chart-config/chart-report";
import { AlarmModel } from "app/models/alarm.model";
import { DataTypeModelKey } from "app/models/dataType.model";
import { DeviceDataModel } from "app/models/deviceData.model";
import { ProjectModel } from "app/models/project.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { AlarmService } from "app/services/api/alarm.service";
import { DataService } from "app/services/api/data.service";
import { ManufacturerService } from "app/services/api/manufacturer.service";
import { ProjectAlarmService } from "app/services/api/projectAlarm.service";
import { ProjectService } from "app/services/api/project.service";
import { buildAlarmThresholdSeries, buildProjectPeriodPlotBands, describeAlarmLimits } from "app/shared/alarm-period-rendering";
import { EmmaDateAdapter } from "app/shared/date.adapter";
import * as Highcharts from "highcharts/highcharts";
import * as moment from "moment";
import { ToastrService } from "ngx-toastr";
import { MAT_DATE_FORMATS, DateAdapter } from "@angular/material/core";
import { Cloudinary } from "@cloudinary/url-gen";
import { CloudinaryImage } from "@cloudinary/url-gen/assets/CloudinaryImage";
import { fill, limitFit } from "@cloudinary/url-gen/actions/resize";
import { circleMarker, latLng, tileLayer } from "leaflet";

type ReportSeriesValueMode = "bucketAverage" | "bucketMax" | "sampleSeries";
type ReportSeriesLineStyle = "Solid" | "ShortDash" | "Dot";

interface ReportSeriesField {
    id: string;
    labelKey: string;
    dataType: DataTypeModelKey;
    mode: ReportSeriesValueMode;
    color: string;
    lineStyle: ReportSeriesLineStyle;
    lineWidth: number;
    zIndex: number;
    enabled: boolean;
}

interface ReportSeriesFieldRow {
    id: string;
    labelKey: string;
    aFieldId: string;
    cFieldId: string;
}

interface ReportLegendItem {
    label?: string;
    labelKey?: string;
    color: string;
    lineStyle: ReportSeriesLineStyle;
}

@Component({
    selector: "app-device-report",
    templateUrl: "./device-report.component.html",
    styleUrls: ["./device-report.component.scss"],
    providers: [
        { provide: DateAdapter, useClass: EmmaDateAdapter },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: "emma",
                    timeInput: "HH:mm",
                },
                display: {
                    dateInput: "emma",
                    timeInput: "HH:mm",
                    monthYearLabel: "MMM YYYY",
                    dateA11yLabel: "LL",
                    monthYearA11yLabel: "MMMM YYYY",
                    timeOptionLabel: "HH:mm",
                },
            },
        },
    ],
    standalone: false,
})
export class DeviceReportComponent implements OnInit {
    @ViewChild("reportContainer") private reportContainerRef?: ElementRef<HTMLElement>;
    @ViewChild("reportTable") private reportTableRef?: ElementRef<HTMLElement>;
    @ViewChild("reportLegend") private reportLegendRef?: ElementRef<HTMLElement>;

    private readonly reportSeriesFieldRowsConfig: ReportSeriesFieldRow[] = [
        {
            id: "1s",
            labelKey: "REPORT.Interval1s",
            aFieldId: "laeq1s",
            cFieldId: "lceq1s",
        },
        {
            id: "5sRolling",
            labelKey: "REPORT.Interval5sRolling",
            aFieldId: "laeq5sRolling",
            cFieldId: "lceq5sRolling",
        },
        {
            id: "5minBucket",
            labelKey: "REPORT.Interval5minBucket",
            aFieldId: "laeq5minBucket",
            cFieldId: "lceq5minBucket",
        },
        {
            id: "5minRolling",
            labelKey: "REPORT.Interval5minRolling",
            aFieldId: "laeq5minRolling",
            cFieldId: "lceq5minRolling",
        },
        {
            id: "30minRolling",
            labelKey: "REPORT.Interval30minRolling",
            aFieldId: "laeq30minRolling",
            cFieldId: "lceq30minRolling",
        },
    ];
    private readonly reportSeriesFieldsConfig: ReportSeriesField[] = [
        {
            id: "laeq1s",
            labelKey: "REPORT.LAeq1s",
            dataType: DataTypeModelKey.soundPressureLevel,
            mode: "sampleSeries",
            color: "#849CBF",
            lineStyle: "Dot",
            lineWidth: 0.75,
            zIndex: 0,
            enabled: false,
        },
        {
            id: "lceq1s",
            labelKey: "REPORT.LCeq1s",
            dataType: DataTypeModelKey.soundPressureLevelC,
            mode: "sampleSeries",
            color: "#5F6B8A",
            lineStyle: "ShortDash",
            lineWidth: 0.75,
            zIndex: 0,
            enabled: false,
        },
        {
            id: "laeq5sRolling",
            labelKey: "REPORT.LAeq5sRolling",
            dataType: DataTypeModelKey.rolling5s,
            mode: "sampleSeries",
            color: "#4F8EF7",
            lineStyle: "ShortDash",
            lineWidth: 1,
            zIndex: 1,
            enabled: false,
        },
        {
            id: "lceq5sRolling",
            labelKey: "REPORT.LCeq5sRolling",
            dataType: DataTypeModelKey.rolling5sC,
            mode: "sampleSeries",
            color: "#1F4BA5",
            lineStyle: "ShortDash",
            lineWidth: 1,
            zIndex: 1,
            enabled: false,
        },
        {
            id: "laeq5minBucket",
            labelKey: "REPORT.LAeq5minBucket",
            dataType: DataTypeModelKey.soundPressureLevel,
            mode: "bucketAverage",
            color: "#5695F5",
            lineStyle: "Solid",
            lineWidth: 1.5,
            zIndex: 2,
            enabled: true,
        },
        {
            id: "lceq5minBucket",
            labelKey: "REPORT.LCeq5minBucket",
            dataType: DataTypeModelKey.soundPressureLevelC,
            mode: "bucketAverage",
            color: "#0F5CC0",
            lineStyle: "ShortDash",
            lineWidth: 1.5,
            zIndex: 2,
            enabled: false,
        },
        {
            id: "laeq5minRolling",
            labelKey: "REPORT.LAeq5minRolling",
            dataType: DataTypeModelKey.rolling5,
            mode: "bucketMax",
            color: "#16A34A",
            lineStyle: "Dot",
            lineWidth: 1.5,
            zIndex: 3,
            enabled: false,
        },
        {
            id: "lceq5minRolling",
            labelKey: "REPORT.LCeq5minRolling",
            dataType: DataTypeModelKey.rolling5C,
            mode: "bucketMax",
            color: "#15803D",
            lineStyle: "ShortDash",
            lineWidth: 1.5,
            zIndex: 3,
            enabled: false,
        },
        {
            id: "laeq30minRolling",
            labelKey: "REPORT.LAeq30minRolling",
            dataType: DataTypeModelKey.rolling30,
            mode: "bucketMax",
            color: "#D97706",
            lineStyle: "Dot",
            lineWidth: 1.5,
            zIndex: 4,
            enabled: false,
        },
        {
            id: "lceq30minRolling",
            labelKey: "REPORT.LCeq30minRolling",
            dataType: DataTypeModelKey.rolling30C,
            mode: "bucketMax",
            color: "#B45309",
            lineStyle: "ShortDash",
            lineWidth: 1.5,
            zIndex: 4,
            enabled: false,
        },
    ];
    readonly reportLineStyleOptions: Array<{ value: ReportSeriesLineStyle; labelKey: string }> = [
        { value: "Solid", labelKey: "REPORT.LineStyleSolid" },
        { value: "ShortDash", labelKey: "REPORT.LineStyleDashed" },
        { value: "Dot", labelKey: "REPORT.LineStyleDotted" },
    ];
    private readonly cloudinary = new Cloudinary({ cloud: { cloudName: "emmadb" } });
    private readonly cloudinaryImageCache = new Map<string, CloudinaryImage>();
    private readonly reportMapTileLayer = tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        attribution: "OpenStreetMap",
    });

    @Input()
    device: ProjectDeviceModel;
    project: ProjectModel;

    EmmaDateAdapter = EmmaDateAdapter;

    generated: boolean = false;

    startDate = moment().subtract(1, "days");
    startTime = moment().hours(7).minutes(0).seconds(0).milliseconds(0);
    endDate = moment();
    endTime = moment().hours(7).minutes(0).seconds(0).milliseconds(0);
    showL10 = true;
    showL90 = true;
    brand: string = "";
    customAlarm: number = 0;
    showComments = true;
    showPeriodBands = true;
    observation: string = "";
    angularDateFormat: string = "dd-MM-yyyy";
    yAxisMin: number = 40;
    yAxisMax: number = 110;

    loadingData = false;
    deviceData: DeviceDataModel[];
    reportSeriesFields = this.reportSeriesFieldsConfig.map((field) => ({ ...field }));
    reportData: Partial<Record<DataTypeModelKey, DeviceDataModel[]>> = {};
    alarms: AlarmModel[] = [];
    private deviceAlarms: AlarmModel[] = [];
    private projectAlarms: AlarmModel[] = [];

    Highcharts = Highcharts; // required
    chart = JSON.parse(JSON.stringify(chartReport));
    chartRef: Highcharts.Chart | null = null;
    reportChartHeight = 320;
    reportMapOptions = {
        layers: [this.reportMapTileLayer],
        zoomControl: false,
        dragging: false,
        doubleClickZoom: false,
        scrollWheelZoom: false,
        boxZoom: false,
        keyboard: false,
        touchZoom: false,
        tap: false,
        attributionControl: false,
    };
    constructor(
        private dataService: DataService,
        private manufacturerService: ManufacturerService,
        private alarmsService: AlarmService,
        private readonly projectAlarmService: ProjectAlarmService,
        private readonly projectService: ProjectService,
        private toastr: ToastrService,
        private router: Router,
        private cdr: ChangeDetectorRef,
        @Inject(LOCALE_ID) public locale: string,
    ) {}

    chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
        this.chartRef = chart;
        requestAnimationFrame(() => this.resizeReportChart());
    };

    private isValidDateFormat(format: string): boolean {
        // Check for at least one year indicator (yyyy or yy)
        const hasYear = /yyyy|yy/.test(format);
        // Check for at least one month indicator (MM, MMM, MMMM, M)
        const hasMonth = /MM|MMM|MMMM|M/.test(format);
        // Check for at least one day indicator (dd or d)
        const hasDay = /dd|d/.test(format);

        return hasYear && hasMonth && hasDay;
    }

    private convertAngularDateFormat(format: string): string {
        if (!format) {
            return "dd-MM-yyyy";
        }
        const convertedFormat = this.project.dateformat
            .replace(/YYYY/g, "yyyy")
            .replace(/YY/g, "yy")
            .replace(/DD/g, "dd")
            .replace(/D/g, "d")
            .replace(/A/g, "a");

        return convertedFormat.length >= 6 && this.isValidDateFormat(convertedFormat) ? convertedFormat : "dd-MM-yyyy";
    }
    ngOnInit() {
        if (!this.device?.id || !this.device?.project) {
            return;
        }
        this.reportSeriesFields = this.reportSeriesFieldsConfig
            .filter((field) => this.isReportFieldAvailable(field))
            .map((field) => ({ ...field }));

        this.projectService.get(this.device.project).subscribe((val) => {
            this.project = val;
            let format = this.project.dateformat;
            if (format) {
                format = format.toUpperCase();
                const formats = format.split(" ");
                EmmaDateAdapter.dateFormat = formats[0];
                const formats2 = this.project.dateformat.split(" ");
                this.angularDateFormat = this.convertAngularDateFormat(formats2[0]);
            }
            // Force date refresh to use the new format
            this.startDate = moment(this.startDate);
            this.endDate = moment(this.endDate);
            this.cdr.detectChanges();
        });

        this.manufacturerService.get(this.device.manufacturer).subscribe((val) => {
            this.brand = val.name;
            this.cdr.detectChanges();
        });

        this.loadAlarms();
    }

    public generateReport() {
        this.generated = false;
        this.loadingData = true;

        if (!this.startTime?.isValid?.()) {
            this.loadingData = false;
            return;
        }
        if (!this.startDate) {
            this.startDate = moment();
        }
        this.startDate.set("hour", this.startTime.hours());
        this.startDate.set("minute", this.startTime.minutes());

        if (!this.endTime?.isValid?.()) {
            this.loadingData = false;
            return;
        }
        if (!this.endDate) {
            this.endDate = moment();
        }
        this.endDate.set("hour", this.endTime.hours());
        this.endDate.set("minute", this.endTime.minutes());

        const selectedFields = this.selectedReportSeriesFields;
        const selectedDataTypes = [...new Set(selectedFields.map((field) => field.dataType))];
        const requiresRawSamples = selectedFields.some((field) => field.mode === "sampleSeries");

        if (selectedDataTypes.length === 0) {
            this.reportData = {};
            this.deviceData = [];
            this.createSensorData();
            this.generated = true;
            this.loadingData = false;
            this.cdr.detectChanges();
            return;
        }

        this.dataService
            .getDeviceDataRange(this.device.id, selectedDataTypes, this.startDate.toDate(), this.endDate.toDate(), !requiresRawSamples)
            .subscribe(
                (data) => {
                    this.reportData = {};
                    selectedDataTypes.forEach((dataType) => {
                        this.reportData[dataType] = this.sortDeviceData(data[dataType] ?? []);
                    });
                    this.deviceData = this.reportData[this.selectedReportSeriesFields[0]?.dataType] ?? [];

                    //Draw the graph
                    this.createSensorData();
                    setTimeout(() => {
                        this.generated = true;
                        this.loadingData = false;
                        this.cdr.detectChanges();
                        requestAnimationFrame(() => this.resizeReportChart());
                    });
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                    if (error.error.statusCode === 404) {
                        this.router.navigate(["/devices"]);
                    }
                    this.loadingData = false;
                    this.cdr.detectChanges();
                },
            );
    }

    createSensorData() {
        if (!this.reportData) {
            return;
        }
        this.chart.options.series = [];

        // Update Y-axis range
        this.chart.options.yAxis[0].min = this.yAxisMin;
        this.chart.options.yAxis[0].softMax = this.yAxisMax;
        this.selectedReportSeriesFields.forEach((field) => {
            const fieldData = this.reportData[field.dataType] ?? [];
            const seriesData = this.buildReportSeriesData(field, fieldData);
            if (seriesData.length === 0) {
                return;
            }

            this.chart.options.series.push({
                id: field.id,
                type: "line",
                data: seriesData,
                name: field.labelKey,
                lineWidth: field.lineWidth,
                color: field.color,
                dashStyle: field.lineStyle,
                visible: true,
                zIndex: field.zIndex,
                marker: {
                    enabled: false,
                },
                states: {
                    hover: {
                        enabled: false,
                    },
                },
                tooltip: {
                    enabled: false,
                },
            });
        });

        this.chart.options.series = this.chart.options.series.filter((series) => !String(series.id ?? "").startsWith("alarm-threshold-"));

        const plotLines = [];
        if (this.customAlarm > 0) {
            plotLines.push({
                value: this.customAlarm,
                color: "red",
                dashStyle: "shortdash",
                width: 1,
                zIndex: 10,
            });
        }
        this.chart.options.yAxis[0].plotLines = plotLines;
        const periodBands = this.showPeriodBands ? buildProjectPeriodPlotBands(this.project, this.startDate, this.endDate) : [];
        this.chart.options.xAxis.plotBands = periodBands;
        this.chart.options.xAxis.plotLines = [];
        this.syncProjectPeriodBands(periodBands);
        const alarmThresholdOptions = {
            projectDeviceId: this.device?.id,
            lf010Settings: this.device?.settings?.lf010,
        };

        this.alarms.forEach((alarm) => {
            if (!alarm.selected) {
                return;
            }

            this.chart.options.series.push({
                id: `alarm-threshold-${alarm.id}`,
                type: "line",
                name: alarm.name,
                data: buildAlarmThresholdSeries(alarm, this.project, this.startDate, this.endDate, alarmThresholdOptions),
                color: "#dc2626",
                dashStyle: "ShortDash",
                lineWidth: 1,
                step: "left",
                zIndex: 10,
                marker: {
                    enabled: false,
                },
                states: {
                    hover: {
                        enabled: false,
                    },
                },
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    crop: false,
                    overflow: "allow",
                    align: "left",
                    x: 3,
                    y: -2,
                    formatter: function () {
                        const points = this.series.points ?? [];
                        const firstPoint = points[0];
                        if (firstPoint !== this.point) {
                            return "";
                        }

                        return `${this.series.name}: ${this.y}`;
                    },
                    style: {
                        color: "#dc2626",
                        fontSize: "11px",
                        fontWeight: "600",
                        textOutline: "none",
                    },
                },
                enableMouseTracking: false,
                tooltip: {
                    enabled: false,
                },
            });
        });

        this.chart.update = true;
        this.chart.visible = true;
    }

    private loadAlarms() {
        this.alarmsService.getAllDevice(this.device.id).subscribe(
            (alarms) => {
                this.deviceAlarms = alarms ?? [];
                this.syncLoadedAlarms();
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.cdr.detectChanges();
            },
        );

        this.projectAlarmService.getAllProject(this.device.project, this.device.id).subscribe(
            (alarms) => {
                this.projectAlarms = alarms ?? [];
                this.syncLoadedAlarms();
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.cdr.detectChanges();
            },
        );
    }

    private syncLoadedAlarms() {
        this.alarms = this.mergeSelectedAlarms([...this.deviceAlarms, ...this.projectAlarms]);
    }

    private mergeSelectedAlarms(alarms: AlarmModel[]) {
        const selectedState = new Map((this.alarms ?? []).map((alarm) => [alarm.id, Boolean(alarm.selected)]));

        return alarms.map((alarm) => ({
            ...alarm,
            selected: selectedState.get(alarm.id) ?? false,
        }));
    }

    private resizeReportChart() {
        const reportContainer = this.reportContainerRef?.nativeElement;
        const reportTable = this.reportTableRef?.nativeElement;
        if (!reportContainer || !reportTable || !this.chartRef) {
            return;
        }

        const reportLegend = this.reportLegendRef?.nativeElement;
        const styles = window.getComputedStyle(reportContainer);
        const verticalPadding = parseFloat(styles.paddingTop || "0") + parseFloat(styles.paddingBottom || "0");
        const legendHeight = reportLegend?.offsetHeight ?? 0;
        const availableHeight = reportContainer.clientHeight - verticalPadding - reportTable.offsetHeight - legendHeight;

        this.reportChartHeight = Math.max(220, Math.floor(availableHeight));
        this.chartRef.setSize(null, this.reportChartHeight, false);
        this.chartRef.reflow();
    }

    get validCoordinates() {
        return this.latitudeValid && this.longitudeValid;
    }

    get availableReportSeriesFields() {
        return this.reportSeriesFields;
    }

    get reportSeriesFieldRows() {
        return this.reportSeriesFieldRowsConfig
            .map((row) => ({
                ...row,
                aField: this.getReportFieldById(row.aFieldId),
                cField: this.getReportFieldById(row.cFieldId),
            }))
            .filter((row) => row.aField || row.cField);
    }

    get selectedReportSeriesFields() {
        return this.reportSeriesFields.filter((field) => field.enabled);
    }

    get reportLegendItems(): ReportLegendItem[] {
        const legendItems: ReportLegendItem[] = this.selectedReportSeriesFields.map((field) => ({
            labelKey: field.labelKey,
            color: field.color,
            lineStyle: field.lineStyle,
        }));

        if (this.customAlarm > 0) {
            legendItems.push({
                labelKey: "REPORT.CustomAlarm",
                color: "#dc2626",
                lineStyle: "ShortDash",
            });
        }

        this.alarms
            ?.filter((alarm) => alarm.selected)
            .forEach((alarm) => {
                legendItems.push({
                    label: alarm.name,
                    color: "#dc2626",
                    lineStyle: "ShortDash",
                });
            });

        return legendItems;
    }

    get reportDateRangeLabel() {
        return `${this.formatReportDate(this.startDate)} ${this.formatReportTime(this.startTime)} - ${this.formatReportDate(this.endDate)} ${this.formatReportTime(this.endTime)}`;
    }

    private getReportFieldById(fieldId: string) {
        return this.reportSeriesFields.find((field) => field.id === fieldId);
    }

    private isReportFieldAvailable(field: ReportSeriesField) {
        return this.device?.fields?.includes(field.dataType) ?? false;
    }

    private sortDeviceData(data: DeviceDataModel[]) {
        return [...data].sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime());
    }

    private buildReportSeriesData(field: ReportSeriesField, data: DeviceDataModel[]) {
        switch (field.mode) {
            case "bucketAverage":
                return data
                    .filter((entry) => entry.aggregation?.average !== undefined)
                    .map((entry) => [new Date(entry.date).getTime(), this.roundValue(entry.aggregation.average)]);
            case "bucketMax":
                return data
                    .filter((entry) => entry.aggregation?.max !== undefined)
                    .map((entry) => [new Date(entry.date).getTime(), this.roundValue(entry.aggregation.max)]);
            case "sampleSeries":
                return data.flatMap((entry) => {
                    if (entry.data?.length) {
                        return entry.data.map((sample) => [new Date(sample.timestamp).getTime(), this.roundValue(sample.value)]);
                    }

                    if (entry.aggregation?.average === undefined) {
                        return [];
                    }

                    return [[new Date(entry.date).getTime(), this.roundValue(entry.aggregation.average)]];
                });
        }
    }

    private roundValue(value: number) {
        return Math.round(value * 10) / 10;
    }

    private syncProjectPeriodBands(plotBands: Highcharts.XAxisPlotBandsOptions[]) {
        const axis = this.chartRef?.xAxis?.[0];
        if (!axis) {
            return;
        }

        const existingBands = (
            axis as Highcharts.Axis & {
                plotLinesAndBands?: Array<{ options?: { id?: string } }>;
            }
        ).plotLinesAndBands;

        existingBands
            ?.filter((band) => band.options?.id?.startsWith("project-period-band-"))
            .forEach((band) => {
                if (band.options?.id) {
                    axis.removePlotBand(band.options.id);
                }
            });

        plotBands.forEach((band) => {
            if (band.id) {
                axis.addPlotBand(band);
            }
        });
    }

    private formatReportDate(value: moment.Moment | null | undefined) {
        return value?.isValid?.() ? value.format(EmmaDateAdapter.dateFormat) : "-";
    }

    private formatReportTime(value: moment.Moment | null | undefined) {
        return value?.isValid?.() ? value.format("HH:mm") : "--:--";
    }

    get latitude() {
        return this.latitudeValid ? this.device.location.latitude : 0;
    }

    get longitude() {
        return this.longitudeValid ? this.device.location.longitude : 0;
    }

    get zoom() {
        return this.validCoordinates ? 16 : 0;
    }

    get reportMapCenter() {
        return latLng(this.latitude, this.longitude);
    }

    get reportMapLayers() {
        if (!this.validCoordinates) {
            return [];
        }

        return [
            circleMarker([this.latitude, this.longitude], {
                radius: 8,
                color: "#ffffff",
                weight: 2,
                fillColor: "#2a3cbe",
                fillOpacity: 1,
            }),
        ];
    }

    get showPicture() {
        return (
            (this.device.picture && this.device.picture.length !== 0) ||
            (this.device.baseDeviceData.picture && this.device.baseDeviceData.picture.length !== 0)
        );
    }

    get picture() {
        if (this.device.picture && this.device.picture.length !== 0) return this.device.picture;
        else return this.device.baseDeviceData.picture;
    }

    get pictureCloudinaryImage(): CloudinaryImage | null {
        return this.getCloudinaryImage(this.picture, null, 160, "limit");
    }

    private get latitudeValid() {
        return this.device.location.latitude && this.device.location.latitude >= -90 && this.device.location.latitude <= 90;
    }

    private get longitudeValid() {
        return this.device.location.longitude && this.device.location.longitude >= -90 && this.device.location.longitude <= 90;
    }

    private momentToAngularFormat(momentFormat: string): string {
        return momentFormat
            .replace(/YYYY/g, "yyyy")
            .replace(/YY/g, "yy")
            .replace(/DD/g, "dd")
            .replace(/D/g, "d")
            .replace(/HH/g, "HH")
            .replace(/H/g, "H")
            .replace(/mm/g, "mm")
            .replace(/m/g, "m")
            .replace(/ss/g, "ss")
            .replace(/s/g, "s")
            .replace(/A/g, "a")
            .replace(/MMMM/g, "MMMM")
            .replace(/MMM/g, "MMM")
            .replace(/MM/g, "MM")
            .replace(/M/g, "M");
    }

    public getCloudinaryImage(
        publicId: string | null | undefined,
        width: number | null = null,
        height: number | null = null,
        crop: "fill" | "limit" = "limit",
    ): CloudinaryImage | null {
        const id = (publicId || "").trim();
        if (!id || id.startsWith("http://") || id.startsWith("https://") || id.startsWith("data:")) {
            return null;
        }

        const key = `${id}|${width ?? ""}|${height ?? ""}|${crop}`;
        const cached = this.cloudinaryImageCache.get(key);
        if (cached) {
            return cached;
        }

        const image = this.cloudinary.image(id).format("auto").quality("auto");

        if (width !== null || height !== null) {
            const resizeAction = crop === "fill" ? fill() : limitFit();
            if (width !== null) {
                resizeAction.width(width);
            }
            if (height !== null) {
                resizeAction.height(height);
            }
            image.resize(resizeAction);
        }

        this.cloudinaryImageCache.set(key, image);
        return image;
    }

    describeAlarmLimits(alarm: AlarmModel): string {
        return describeAlarmLimits(alarm, this.project);
    }
}
